import { kbFocusHighlighterIgnoreClass } from "app/client/components/KeyboardFocusHighlighter";
import { onceAttached } from "app/client/lib/domUtils";
import { makeT } from "app/client/lib/localization";
import { createTypewriterText } from "app/client/lib/typewriterText";
import { PromptCategory } from "app/client/ui/AssistantPrompts";
import { autoGrow } from "app/client/ui/forms";
import { reveal, revealed } from "app/client/ui/HomeSection";
import { theme, vars } from "app/client/ui2018/cssVars";
import { icon } from "app/client/ui2018/icons";
import { loadingSpinner } from "app/client/ui2018/loaders";
import { gristFloatingMenuClass, menu, menuCssClass, menuItem } from "app/client/ui2018/menus";
import { useBindable } from "app/common/gutil";

import {
  BindableValue, Computed, dom, DomContents, IDisposableOwner, keyframes, makeTestId, Observable,
  styled,
} from "grainjs";

// eslint-disable-next-line local/makeT-filename
const t = makeT("Assistant");

const testId = makeTestId("test-assistant-");

interface AssistantStartBlockOptions {
  prompts: PromptCategory[];
  placeholder: string;
  heading?: string;
  showIntro: boolean;
  revealDelayMs?: number;
  enabled?: BindableValue<boolean>;
  disabledPlaceholder?: string;
  autoFocus?: boolean;
  pending?: Observable<boolean>;
  errorText?: Observable<string>;
  onSubmit: (prompt: string) => void;
}

function interleavePrompts(categories: PromptCategory[]): string[] {
  const result: string[] = [];
  const longest = Math.max(0, ...categories.map(c => c.prompts.length));
  for (let i = 0; i < longest; i++) {
    for (const category of categories) {
      const prompt = category.prompts[i];
      if (prompt !== undefined) { result.push(prompt); }
    }
  }
  return result;
}

/**
 * Builds a composer for starting a conversation with the assistant.
 *
 * Includes a set of sample prompts, grouped by category, displayed as pills
 * that open a menu of prompts on click. Hovering a prompt previews it in the
 * composer's placeholder.
 */
export function buildAssistantStartBlock(
  owner: IDisposableOwner, options: AssistantStartBlockOptions,
): DomContents {
  const { prompts, showIntro, onSubmit, errorText } = options;
  const pending = options.pending ?? Observable.create(owner, false);
  const enabled = Computed.create(owner, use =>
    options.enabled === undefined ? true : useBindable(use, options.enabled));
  const busy = Computed.create(owner, use => use(pending) || !use(enabled));

  const boxDelayMs = options.revealDelayMs ?? 0;
  const rowDelayMs = boxDelayMs + 80;

  const draft = Observable.create(owner, "");
  const hasText = Computed.create(owner, use => use(draft).trim() !== "");
  const preview = Observable.create<string | null>(owner, null);
  const typed = Observable.create<string | null>(owner, null);
  const placeholder = Computed.create(owner, (use) => {
    if (!use(enabled)) {
      return options.disabledPlaceholder ?? t("The assistant is not available here.");
    }
    return use(preview) ?? use(typed) ?? options.placeholder;
  });

  if (showIntro) {
    const typewriter = createTypewriterText({
      phrases: interleavePrompts(prompts),
      onText: text => typed.set(text),
    });
    owner.autoDispose(typewriter);
    const idle = Computed.create(owner, use =>
      !use(hasText) && use(preview) === null && !use(busy));
    owner.autoDispose(idle.addListener(on => on ? typewriter.start() : typewriter.stop()));
    if (idle.get()) { typewriter.start(); }
  }

  function send(prompt: string) {
    const text = prompt.trim();
    if (!text || busy.get()) { return; }
    preview.set(null);
    onSubmit(text);
  }

  const input = cssInput(
    { "rows": "1", "aria-label": t("Message input") },
    dom.cls(kbFocusHighlighterIgnoreClass),
    dom.attr("placeholder", placeholder),
    dom.boolAttr("disabled", busy),
    dom.on("input", (_ev, elem) => draft.set(elem.value)),
    autoGrow(draft),
    dom.onKeyDown({
      Enter$: (ev) => {
        if (ev.shiftKey || ev.isComposing) { return; }
        ev.preventDefault();
        send(input.value);
      },
    }),
    elem => onceAttached(elem, () => {
      if (options.autoFocus) { elem.focus({ preventScroll: true }); }
    }),
  );

  const cannotSend = Computed.create(owner, use => use(busy) || !use(hasText));

  const sendButton = cssSendButton(
    { "type": "button", "aria-label": t("Send message") },
    reveal(showIntro, rowDelayMs + prompts.length * 40),
    dom.boolAttr("disabled", cannotSend),
    cssSendButton.cls("-pending", pending),
    dom.domComputed(pending, isPending => isPending ?
      cssSendSpinner() :
      cssSendIcon("ArrowRight")),
    dom.on("click", () => send(input.value)),
    testId("send"),
  );

  function buildPill(category: PromptCategory, index: number) {
    return cssPill(
      category.label,
      reveal(showIntro, rowDelayMs + index * 40),
      { "type": "button", "aria-haspopup": "menu", "data-category": category.id },
      menu((ctl) => {
        ctl.onDispose(() => { if (!preview.isDisposed()) { preview.set(null); } });
        return category.prompts.map(prompt => menuItem(
          () => send(prompt),
          prompt,
          dom.cls("disabled", busy),
          elem => dom.dataElem(elem, "menuItemSelected",
            (selected: boolean) => preview.set(selected ? prompt : null)),
          testId("start-prompt"),
        ));
      }, {
        placement: "bottom-start",
        menuCssClass: `${menuCssClass} ${gristFloatingMenuClass} ${cssPromptMenu.className}`,
      }),
      testId("start-pill"),
    );
  }

  const composer = cssComposer(
    reveal(showIntro, boxDelayMs),
    input,
    cssComposerFooter(
      cssPills(
        prompts.map(buildPill),
        testId("start-pills"),
      ),
      sendButton,
    ),
    testId("input"),
  );

  return cssBlock(
    options.heading ?
      cssHeading(options.heading, reveal(showIntro, 0), testId("start-heading")) :
      null,
    composer,
    errorText ? dom.maybe(errorText, text => cssError(text, testId("error"))) : null,
    testId("start"),
  );
}

const cssHeading = styled("div", `
  margin-bottom: 1rem;
  color: ${theme.text};
  font-size: ${vars.xxlargeFontSize};
  line-height: 1.3;
  text-align: center;
  ${revealed}
`);

const cssBlock = styled("div", `
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  color: ${theme.text};
`);

const cssComposer = styled("div", `
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  padding: 0 0.5rem;
  border: 1px solid ${theme.inputBorder};
  border-radius: 12px;
  background-color: ${theme.inputBg};
  transition: box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1);
  ${revealed}

  &:focus-within {
    border-color: ${theme.controlPrimaryBg};
    box-shadow: 0 0 0 1px ${theme.controlPrimaryBg};
  }
`);

const cssInput = styled("textarea", `
  flex: 1 1 auto;
  box-sizing: border-box;
  width: auto;
  min-height: calc(1.375rem + 3em);
  max-height: 13rem;
  padding: 1rem 0.75rem 0.375rem 1.125rem;
  border: none;
  outline: none;
  resize: none;
  overflow-y: auto;
  background-color: transparent;
  color: ${theme.inputFg};
  font-family: inherit;
  font-size: ${vars.introFontSize};
  line-height: 1.5;
  scrollbar-width: thin;

  &::placeholder {
    color: ${theme.inputPlaceholderFg};
    overflow: hidden;
  }
  &:disabled {
    cursor: default;
  }
`);

const cssComposerFooter = styled("div", `
  flex: none;
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  min-height: 3.25rem;
  padding: 0.25rem 0.5rem 0.75rem 0.625rem;
`);

const cssSendButton = styled("button", `
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  border: none;
  border-radius: 9999px;
  background-color: ${theme.controlPrimaryBg};
  cursor: pointer;
  transition: background-color 150ms ease-out;
  ${revealed}

  @media (prefers-reduced-motion: reduce) {
    & {
      transition: none;
    }
  }
  &:hover:not(:disabled) {
    background-color: ${theme.controlPrimaryHoverBg};
  }
  &:disabled {
    background-color: ${theme.controlDisabledBg};
    cursor: default;
  }
  &-pending:disabled {
    background-color: ${theme.controlPrimaryBg};
  }
`);

const cssSendIcon = styled(icon, `
  width: 1.125rem;
  height: 1.125rem;
  background-color: ${theme.controlPrimaryFg};
  transform: rotate(-90deg);

  .${cssSendButton.className}:disabled & {
    background-color: ${theme.controlDisabledFg};
  }
`);

const cssSendSpinner = styled(loadingSpinner, `
  --loader-bg: transparent;
  --loader-fg: ${theme.controlPrimaryFg};
  width: 1rem;
  height: 1rem;
  border-width: 2px;
`);

const cssError = styled("div", `
  width: 100%;
  margin-top: 0.5rem;
  color: ${theme.errorText};
  font-size: 0.8125rem;
  line-height: 1.625;
`);

const cssPills = styled("div", `
  flex: 1 1 auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 0.5rem;
  min-width: 0;
`);

const cssPill = styled("button", `
  max-width: 100%;
  padding: 4px 12px;
  border: 1px solid ${theme.inputBorder};
  border-radius: ${vars.controlBorderRadius};
  background-color: ${theme.pageBg};
  color: ${theme.text};
  font-family: inherit;
  font-size: ${vars.mediumFontSize};
  line-height: 16px;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 150ms ease-out, border-color 150ms ease-out;
  ${revealed}

  &:hover, &.weasel-popup-open {
    background-color: ${theme.hover};
  }
  &:focus-visible {
    outline: 2px solid ${theme.controlPrimaryBg};
    outline-offset: 2px;
  }
`);

const menuUnfold = keyframes(`
  from { opacity: 0; transform: scale(0.96); }
  to { opacity: 1; transform: none; }
`);

const cssPromptMenu = styled("div", `
  width: min(28rem, calc(100vw - 32px));
  max-width: none;
  padding: 8px 0;
  transform-origin: top center;
  animation: ${menuUnfold} 150ms cubic-bezier(0, 0, 0.2, 1) backwards;

  & > * {
    white-space: normal;
    line-height: 1.5;
  }
  @media (prefers-reduced-motion: reduce) {
    & {
      animation: none;
    }
  }
`);
